import { useMemo } from 'react';
import { useAuth } from './useAuth';

/**
 * Re-export getEffectiveTeamId utility for convenience
 */
export { getEffectiveTeamId } from '@/lib/queryKeys';

/**
 * Return value from useEffectiveTeamId hook
 */
export interface UseEffectiveTeamIdReturn {
  /**
   * Effective team ID for the current user
   * - Returns currentTeamId if in God Mode (agency staff viewing client workspace)
   * - Returns teamId for regular users
   * - Returns null if user has no team (e.g., agency staff without home team)
   */
  teamId: number | null;

  /**
   * Whether the user has an effective team
   * - true if teamId is not null
   * - false if user has no team (should disable team-scoped queries)
   */
  hasTeam: boolean;

  /**
   * Whether the user is agency staff
   * - true if user.isAgencyStaff is true
   * - false otherwise
   */
  isAgencyStaff: boolean;

  /**
   * Whether the user is currently in God Mode
   * - true if an active godModeSession is present on the user object
   * - false for regular users, agency staff in their home team, or orphaned currentTeamId pointers
   */
  isInGodMode: boolean;
}

/**
 * useEffectiveTeamId Hook
 *
 * Provides the effective team ID for the current user, handling God Mode
 * (when agency staff view client workspaces) and edge cases.
 *
 * **What is "Effective Team"?**
 * - For regular users: their home `teamId`
 * - For agency staff in God Mode: `currentTeamId` (client's team)
 * - For agency staff not in God Mode: their home `teamId` (may be null)
 *
 * **God Mode:**
 * When agency staff click "Enter" on a client workspace:
 * 1. `currentTeamId` is set to the client's team ID
 * 2. All team-scoped queries use `currentTeamId` instead of `teamId`
 * 3. Posts/content created belong to the client's team, authored by staff user
 * 4. When exiting God Mode, `currentTeamId` is reset to null
 *
 * **Edge Cases:**
 * - **Agency staff without home team**: `teamId` is null, but they can enter client workspaces
 *   - In God Mode: `teamId = currentTeamId` (client's team)
 *   - Not in God Mode: `teamId = null` (no team, queries disabled)
 * - **Regular users**: `currentTeamId` is always null
 * - **No authentication**: `teamId = null`
 *
 * **Usage with React Query:**
 * ```typescript
 * const { teamId, hasTeam, isInGodMode } = useEffectiveTeamId();
 *
 * // Fetch posts for current team
 * const { data, isLoading } = useQuery({
 *   queryKey: teamQueryKeys.posts.list(teamId),
 *   queryFn: () => postsService.getAll(teamId),
 *   enabled: hasTeam, // Disable query if no team
 * });
 *
 * // Show banner when in God Mode
 * {isInGodMode && <GodModeBanner />}
 * ```
 *
 * **Switching Teams (God Mode):**
 * When entering/exiting God Mode, `currentTeamId` changes, causing:
 * - Hook to return new `teamId`
 * - All team-scoped query keys to change
 * - React Query to automatically refetch data for new team
 *
 * @returns {UseEffectiveTeamIdReturn} Effective team ID and related flags
 *
 * @example
 * // Regular user with home team
 * const { teamId, hasTeam, isAgencyStaff, isInGodMode } = useEffectiveTeamId();
 * // teamId = 42, hasTeam = true, isAgencyStaff = false, isInGodMode = false
 *
 * @example
 * // Agency staff in God Mode (viewing client workspace)
 * const { teamId, hasTeam, isAgencyStaff, isInGodMode } = useEffectiveTeamId();
 * // teamId = 123 (client's team), hasTeam = true, isAgencyStaff = true, isInGodMode = true
 *
 * @example
 * // Agency staff not in God Mode (no home team)
 * const { teamId, hasTeam, isAgencyStaff, isInGodMode } = useEffectiveTeamId();
 * // teamId = null, hasTeam = false, isAgencyStaff = true, isInGodMode = false
 */
export const useEffectiveTeamId = (): UseEffectiveTeamIdReturn => {
  const { user } = useAuth();

  return useMemo(() => {
    // Calculate effective team ID (God Mode takes precedence)
    const teamId = user?.currentTeamId || user?.teamId || null;

    // Check if user has a team
    const hasTeam = teamId !== null;

    // Check if user is agency staff
    const isAgencyStaff = user?.isAgencyStaff || false;

    // Check if user is in God Mode.
    // We previously derived this from pointer comparison (currentTeamId !== teamId), but
    // currentTeamId can be orphaned in the DB (session expired without exit, server crash,
    // raw SQL force-exit, migration drift). Use the canonical session object so a stale
    // pointer doesn't make the UI lie. Backend self-heal in /me clears orphaned pointers,
    // but this read-side check is the second line of defense.
    const isInGodMode = !!user?.godModeSession;

    return {
      teamId,
      hasTeam,
      isAgencyStaff,
      isInGodMode,
    };
  }, [user?.currentTeamId, user?.teamId, user?.isAgencyStaff, user?.godModeSession]);
};
